import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import {
  Loader2, Upload, CheckCircle2, AlertCircle,
  Image as ImageIcon, FileText, Crop, FlipHorizontal, FlipVertical,
} from 'lucide-react';
import { getLayouts, getPhotoMarkers, saveLayoutPhoto } from '../api/layout-api';
import { attachPdfToLayout, copyPdfLayoutToLayout } from '../api/layout-pdf-api';
import { listPDFs, listPDFPages, PDFFileList } from '@/features/pdf-editor/api/index';
import { DrawingCropPicker } from './DrawingCropPicker';
import { CropRegion } from '../utils/cropPdfRegion';
import { Layout } from '../types/types';

type ActiveTab = 'upload' | 'from-drawing';
type CreateFromDrawingMode = 'crop' | 'copy';

interface AddLayoutPhotoDialogProps {
  open: boolean;
  layoutId: string;
  layoutName: string;
  projectId?: string;
  layouts?: Layout[];
  isSuperuser?: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const EMPTY_LAYOUTS: Layout[] = [];

export const AddLayoutPhotoDialog: React.FC<AddLayoutPhotoDialogProps> = ({
  open,
  layoutId,
  layoutName,
  projectId,
  layouts = EMPTY_LAYOUTS,
  isSuperuser = false,
  onOpenChange,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('upload');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Upload tab state ──
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<'image' | 'pdf' | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── From-drawing tab state ──
  const [drawings, setDrawings] = useState<PDFFileList[]>([]);
  const [drawingsLoading, setDrawingsLoading] = useState(false);
  const [selectedDrawingUrl, setSelectedDrawingUrl] = useState<string | null>(null);
  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(null);
  const [selectedDrawingRotation, setSelectedDrawingRotation] = useState(0);
  const [cropRegion, setCropRegion] = useState<CropRegion | null>(null);
  const [createMode, setCreateMode] = useState<CreateFromDrawingMode>('crop');
  const [selectedSourceLayoutId, setSelectedSourceLayoutId] = useState<string | null>(null);
  const [sourceMarkerCount, setSourceMarkerCount] = useState<number | null>(null);
  const [sourceMarkersLoading, setSourceMarkersLoading] = useState(false);
  const [copyMarkers, setCopyMarkers] = useState(false);
  const [flippedHorizontal, setFlippedHorizontal] = useState(false);
  const [flippedVertical, setFlippedVertical] = useState(false);
  const [projectLayouts, setProjectLayouts] = useState<Layout[] | null>(null);
  const [projectLayoutsLoading, setProjectLayoutsLoading] = useState(false);

  // ── Shared state ──
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const copyableLayoutsSource = projectLayouts ?? layouts;

  const copyableLayouts = useMemo(
    () => copyableLayoutsSource.filter((layout) =>
      String(layout.id) !== String(layoutId) &&
      Boolean(layout.has_pdf && layout.pdf_page_id)
    ),
    [copyableLayoutsSource, layoutId]
  );

  const selectedSourceLayout = useMemo(
    () => copyableLayouts.find((layout) => String(layout.id) === String(selectedSourceLayoutId)) ?? null,
    [copyableLayouts, selectedSourceLayoutId]
  );

  const getLayoutOptionLabel = useCallback((layout: Layout) => (
    layout.type ? `${layout.name} (${layout.type})` : layout.name
  ), []);

  useEffect(() => {
    if (!open) return;

    setProjectLayouts(layouts);

    if (!projectId) {
      setProjectLayoutsLoading(false);
      return;
    }

    let cancelled = false;
    setProjectLayoutsLoading(true);

    getLayouts(projectId)
      .then((projectLayoutList) => {
        if (!cancelled) setProjectLayouts(projectLayoutList);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load layouts for this project.');
      })
      .finally(() => {
        if (!cancelled) setProjectLayoutsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectId, layouts]);

  // ── Load drawings when switching to the from-drawing tab ──
  const handleTabChange = useCallback(async (tab: ActiveTab) => {
    setActiveTab(tab);
    setError(null);
    if (tab === 'from-drawing' && drawings.length === 0 && projectId) {
      setDrawingsLoading(true);
      try {
        const list = await listPDFs(parseInt(projectId));
        setDrawings(list);
      } catch {
        setError('Failed to load drawings for this project.');
      } finally {
        setDrawingsLoading(false);
      }
    }
  }, [drawings.length, projectId]);

  // ── Upload tab handlers ──
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPdf   = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/');

    if (!isPdf && !isImage) { setError('Please select an image or PDF file'); return; }

    const maxSize = isPdf ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File size must be less than ${isPdf ? '50MB' : '10MB'}`);
      return;
    }

    setSelectedFile(file);
    setFileType(isPdf ? 'pdf' : 'image');
    setError(null);

    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !fileType) { setError('Please select a file'); return; }

    try {
      setIsUploading(true);
      setError(null);

      if (fileType === 'image') {
        await saveLayoutPhoto(layoutId, selectedFile);
      } else {
        if (!projectId) throw new Error('Project ID is required for PDF uploads');
        await attachPdfToLayout(layoutId, selectedFile, {}, parseInt(projectId));
      }

      setSuccess(true);
      setTimeout(() => { onSuccess(); handleClose(); }, 1000);
    } catch (err: any) {
      setError(err?.data?.error || err?.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  // ── From-drawing tab handlers ──
  const handleDrawingSelect = useCallback(async (drawing: PDFFileList) => {
    setSelectedDrawingId(drawing.id);
    setSelectedDrawingUrl(drawing.file_url);
    setSelectedDrawingRotation(0);
    setCropRegion(null);
    setError(null);
    try {
      const pageRows = await listPDFPages(drawing.id, 1);
      const pageOne = pageRows.find((row) => row.page_number === 1) ?? pageRows[0];
      const rawRotation = pageOne?.rotation ?? 0;
      setSelectedDrawingRotation(((rawRotation % 360) + 360) % 360);
    } catch (rotationError) {
      console.warn('Unable to load drawing rotation for crop picker.', rotationError);
    }
  }, []);

  const handleCreateFromDrawing = async () => {
    if (createMode === 'copy') {
      if (!selectedSourceLayoutId) {
        setError('Please select an existing PDF layout to copy.');
        return;
      }

      try {
        setIsUploading(true);
        setError(null);

        await copyPdfLayoutToLayout(
          layoutId,
          selectedSourceLayoutId,
          Boolean(copyMarkers && (sourceMarkerCount ?? 0) > 0),
          { flippedHorizontal, flippedVertical }
        );

        setSuccess(true);
        setTimeout(() => { onSuccess(); handleClose(); }, 1000);
      } catch (err: any) {
        setError(err?.data?.error || err?.message || 'Failed to copy layout PDF settings.');
      } finally {
        setIsUploading(false);
      }
      return;
    }

    if (!selectedDrawingId || !cropRegion) {
      setError('Please select a drawing and drag to choose a region.');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      await attachPdfToLayout(layoutId, selectedDrawingId, {
        pageNumber: 1,
        cropRegion: [cropRegion.x, cropRegion.y, cropRegion.width, cropRegion.height],
        flippedHorizontal,
        flippedVertical,
      });

      setSuccess(true);
      setTimeout(() => { onSuccess(); handleClose(); }, 1000);
    } catch (err: any) {
      setError(err?.message || 'Failed to create layout from drawing.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSourceLayoutSelect = useCallback(async (sourceLayoutId: string) => {
    setSelectedSourceLayoutId(sourceLayoutId || null);
    setSourceMarkerCount(null);
    setCopyMarkers(false);
    setError(null);

    if (!sourceLayoutId) return;

    setSourceMarkersLoading(true);
    try {
      const markers = await getPhotoMarkers(sourceLayoutId);
      setSourceMarkerCount(markers.length);
      setCopyMarkers(markers.length > 0);
    } catch {
      setError('Failed to load photo marker count for the selected layout.');
    } finally {
      setSourceMarkersLoading(false);
    }
  }, []);

  const handleClose = () => {
    setSelectedFile(null);
    setFileType(null);
    setPreviewUrl(null);
    setDrawings([]);
    setSelectedDrawingId(null);
    setSelectedDrawingUrl(null);
    setSelectedDrawingRotation(0);
    setCropRegion(null);
    setCreateMode('crop');
    setSelectedSourceLayoutId(null);
    setSourceMarkerCount(null);
    setSourceMarkersLoading(false);
    setCopyMarkers(false);
    setFlippedHorizontal(false);
    setFlippedVertical(false);
    setProjectLayouts(null);
    setProjectLayoutsLoading(false);
    setError(null);
    setSuccess(false);
    setActiveTab('upload');
    setIsFullscreen(false);
    onOpenChange(false);
  };

  const canSubmit = activeTab === 'upload'
    ? Boolean(selectedFile && fileType) && !isUploading && !success
    : createMode === 'copy'
      ? Boolean(selectedSourceLayoutId) && !sourceMarkersLoading && !isUploading && !success
      : Boolean(selectedDrawingUrl && cropRegion) && !isUploading && !success;

  const dialogContentClass = isFullscreen
    ? 'inset-4 max-w-none w-auto translate-x-0 translate-y-0 left-4 right-4 top-4 bottom-4 max-h-none overflow-hidden flex flex-col'
    : 'max-w-3xl flex flex-col';

  const dialogContentStyle = isFullscreen
    ? {}
    : { maxHeight: '90vh' };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={dialogContentClass} style={dialogContentStyle}>
        <DialogHeader>
          <DialogTitle>Add Layout Drawing</DialogTitle>
          <DialogDescription>
            Add a drawing for layout: <strong>{layoutName}</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Tab switcher */}
        <div className="flex border-b border-border -mx-1 shrink-0">
          <button
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'upload'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => handleTabChange('upload')}
          >
            <Upload className="h-4 w-4" />
            Upload File
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'from-drawing'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => handleTabChange('from-drawing')}
            disabled={!projectId}
            title={!projectId ? 'Project ID required' : undefined}
          >
            <Crop className="h-4 w-4" />
            Create from Drawing
          </button>
        </div>

        {/* Tab content */}
        <div
          className={`flex-1 py-4 ${activeTab === 'from-drawing' && isFullscreen ? 'flex flex-col gap-4' : 'overflow-y-auto space-y-4'}`}
          style={{ minHeight: 0 }}
        >

          {/* ── Upload tab ── */}
          {activeTab === 'upload' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="photo">Select File</Label>
                <Input
                  ref={fileInputRef}
                  id="photo"
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  onChange={handleFileSelect}
                  disabled={isUploading || success}
                />
                <p className="text-sm text-muted-foreground">
                  Accepted: JPG, PNG, GIF, WebP (max 10 MB) or PDF (max 50 MB)
                </p>
              </div>

              {previewUrl && fileType === 'image' && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border rounded-lg overflow-hidden bg-muted/50 flex items-center justify-center max-h-72">
                    <img src={previewUrl} alt="Preview" className="max-w-full max-h-72 object-contain" />
                  </div>
                </div>
              )}

              {selectedFile && fileType === 'pdf' && (
                <div className="space-y-2">
                  <Label>Selected PDF</Label>
                  <div className="border rounded-lg p-6 bg-muted/50 flex items-center space-x-4">
                    <FileText className="h-12 w-12 text-primary shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                    </div>
                  </div>
                </div>
              )}

              {!selectedFile && !success && (
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <div className="flex justify-center space-x-4 mb-4">
                    <ImageIcon className="h-12 w-12 text-muted-foreground" />
                    <FileText className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Click "Choose File" above to select a layout image or PDF drawing
                  </p>
                </div>
              )}
            </>
          )}

          {/* ── From-drawing tab ── */}
          {activeTab === 'from-drawing' && (
            <>
              {isSuperuser && (
                <div className="space-y-2 shrink-0">
                  <Label>Create from drawing option</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        createMode === 'crop'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-border/80 hover:bg-accent'
                      }`}
                      onClick={() => {
                        setCreateMode('crop');
                        setError(null);
                      }}
                      disabled={isUploading}
                    >
                      <p className="font-medium text-sm text-foreground">Crop from drawing</p>
                      <p className="text-xs text-muted-foreground">Pick a drawing and crop a region.</p>
                    </button>
                    <button
                      type="button"
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        createMode === 'copy'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-border/80 hover:bg-accent'
                      }`}
                      onClick={() => {
                        setCreateMode('copy');
                        setIsFullscreen(false);
                        setError(null);
                      }}
                      disabled={isUploading}
                    >
                      <p className="font-medium text-sm text-foreground">Copy from existing layout</p>
                      <p className="text-xs text-muted-foreground">Reuse a PDF page and crop coordinates.</p>
                    </button>
                  </div>
                </div>
              )}

              {createMode === 'copy' && (
                <div className="space-y-3 shrink-0">
                  <div className="space-y-2">
                    <Label htmlFor="source-layout">Select layout to copy</Label>
                    {projectLayoutsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading project layouts...
                      </div>
                    ) : copyableLayouts.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No other PDF-backed layouts are available in this project.
                      </p>
                    ) : (
                      <select
                        id="source-layout"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={selectedSourceLayoutId ?? ''}
                        onChange={(event) => handleSourceLayoutSelect(event.target.value)}
                        disabled={isUploading}
                      >
                        <option value="">Choose a layout...</option>
                        {copyableLayouts.map((layout) => (
                          <option key={layout.id} value={layout.id}>
                            {getLayoutOptionLabel(layout)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {sourceMarkersLoading && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking photo markers...
                    </div>
                  )}

                  {!sourceMarkersLoading && selectedSourceLayout && (sourceMarkerCount ?? 0) > 0 && (
                    <label className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={copyMarkers}
                        onChange={(event) => setCopyMarkers(event.target.checked)}
                        disabled={isUploading}
                      />
                      <span>
                        Copy photo markers with layout image? ({sourceMarkerCount}) markers on {selectedSourceLayout.name}
                      </span>
                    </label>
                  )}

                  {selectedSourceLayout && (
                    <div className="space-y-2">
                      <Label>Flip copied layout</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          type="button"
                          className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                            flippedHorizontal
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-border hover:border-border/80 hover:bg-accent'
                          }`}
                          onClick={() => setFlippedHorizontal((value) => !value)}
                          disabled={isUploading}
                          aria-pressed={flippedHorizontal}
                        >
                          <FlipHorizontal className="h-4 w-4 shrink-0" />
                          <span>Flip horizontal</span>
                        </button>
                        <button
                          type="button"
                          className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                            flippedVertical
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-border hover:border-border/80 hover:bg-accent'
                          }`}
                          onClick={() => setFlippedVertical((value) => !value)}
                          disabled={isUploading}
                          aria-pressed={flippedVertical}
                        >
                          <FlipVertical className="h-4 w-4 shrink-0" />
                          <span>Flip vertical</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {createMode === 'crop' && selectedDrawingUrl && !isFullscreen && (
                <div className="space-y-2 shrink-0">
                  <Label>Flip cropped layout</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                        flippedHorizontal
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border hover:border-border/80 hover:bg-accent'
                      }`}
                      onClick={() => setFlippedHorizontal((value) => !value)}
                      disabled={isUploading}
                      aria-pressed={flippedHorizontal}
                    >
                      <FlipHorizontal className="h-4 w-4 shrink-0" />
                      <span>Flip horizontal</span>
                    </button>
                    <button
                      type="button"
                      className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                        flippedVertical
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border hover:border-border/80 hover:bg-accent'
                      }`}
                      onClick={() => setFlippedVertical((value) => !value)}
                      disabled={isUploading}
                      aria-pressed={flippedVertical}
                    >
                      <FlipVertical className="h-4 w-4 shrink-0" />
                      <span>Flip vertical</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 1: pick a drawing — hidden in fullscreen to maximise canvas */}
              {createMode === 'crop' && !isFullscreen && (
                <div className="space-y-2 shrink-0">
                  <Label>Step 1 — Select a drawing</Label>
                  {drawingsLoading && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading drawings…
                    </div>
                  )}
                  {!drawingsLoading && drawings.length === 0 && !error && (
                    <p className="text-sm text-muted-foreground">
                      No drawings found for this project. Upload a PDF drawing first.
                    </p>
                  )}
                  {!drawingsLoading && drawings.length > 0 && (
                    <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto pr-1">
                      {drawings.map((d) => (
                        <button
                          key={d.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                            selectedDrawingId === d.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-border/80 hover:bg-accent'
                          }`}
                          onClick={() => handleDrawingSelect(d)}
                          disabled={isUploading}
                        >
                          <FileText className={`h-8 w-8 shrink-0 ${selectedDrawingId === d.id ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-foreground truncate">{d.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {d.page_count} page{d.page_count !== 1 ? 's' : ''} ·{' '}
                              {new Date(d.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {selectedDrawingId === d.id && (
                            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: crop picker */}
              {createMode === 'crop' && selectedDrawingUrl && (
                <div className={`space-y-1 ${isFullscreen ? 'flex-1 flex flex-col' : ''}`} style={isFullscreen ? { minHeight: 0 } : {}}>
                  {!isFullscreen && <Label>Step 2 — Drag to select the region</Label>}
                  <div className={isFullscreen ? 'flex-1' : ''} style={isFullscreen ? { minHeight: 0 } : { height: 380 }}>
                    <DrawingCropPicker
                      fileUrl={selectedDrawingUrl}
                      rotation={selectedDrawingRotation}
                      onRegionChange={setCropRegion}
                      isFullscreen={isFullscreen}
                      onToggleFullscreen={() => setIsFullscreen(f => !f)}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Shared status messages ── */}
          {success && (
            <Alert className="border-primary/20 bg-primary/5 shrink-0">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <AlertDescription className="text-foreground">
                Layout drawing created successfully! The viewer will refresh automatically.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="shrink-0">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="border-t pt-4 shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={activeTab === 'upload' ? handleUpload : handleCreateFromDrawing}
            disabled={!canSubmit}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {activeTab === 'upload' ? 'Uploading…' : 'Creating…'}
              </>
            ) : activeTab === 'upload' ? (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload {fileType === 'image' ? 'Photo' : 'PDF'}
              </>
            ) : (
              <>
                <Crop className="mr-2 h-4 w-4" />
                {createMode === 'copy' ? 'Copy Layout' : 'Create Layout'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
